import React, { useEffect, useRef, useState } from 'react';

import styles from './SendEmailModal.module.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_SUBJECT_LEN = 120;

export interface SendEmailModalProps {
  open: boolean;
  defaultSubject: string;
  defaultTo: string;
  onCancel: () => void;
  onSend: (subject: string, to: string) => Promise<void> | void;
}

export const SendEmailModal: React.FC<SendEmailModalProps> = ({
  open,
  defaultSubject,
  defaultTo,
  onCancel,
  onSend,
}) => {
  const [subject, setSubject] = useState<string>(defaultSubject);
  const [to, setTo] = useState<string>(defaultTo);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const subjectRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
    setTo(defaultTo);
    setSubmitError(null);
    setShowErrors(false);
    setSubmitting(false);
    const id = window.setTimeout(() => {
      subjectRef.current?.focus();
      subjectRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, defaultSubject, defaultTo]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const trimmedSubject = subject.trim();
  const trimmedTo = to.trim();
  const subjectError = trimmedSubject.length === 0
    ? 'Subject is required.'
    : trimmedSubject.length > MAX_SUBJECT_LEN
      ? `Subject must be at most ${MAX_SUBJECT_LEN} characters.`
      : null;
  const toError = trimmedTo.length === 0
    ? 'Recipient email is required.'
    : !EMAIL_REGEX.test(trimmedTo)
      ? 'Recipient email is not valid.'
      : null;
  const hasErrors = subjectError !== null || toError !== null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSend(trimmedSubject, trimmedTo);
    } catch (caught: unknown) {
      const message = caught instanceof Error ? caught.message : 'Failed to send email.';
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="send-email-modal"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-email-modal-title"
      >
        <form onSubmit={handleSubmit} noValidate>
          <h2 id="send-email-modal-title" className={styles.title}>
            Send dashboard email
          </h2>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="send-email-subject">
              Subject
            </label>
            <input
              id="send-email-subject"
              ref={subjectRef}
              className={`${styles.input} ${showErrors && subjectError ? styles.inputInvalid : ''}`}
              type="text"
              value={subject}
              maxLength={MAX_SUBJECT_LEN + 1}
              onChange={(event) => setSubject(event.target.value)}
              autoComplete="off"
              data-testid="send-email-subject"
            />
            {showErrors && subjectError ? (
              <span className={styles.fieldError} data-testid="send-email-subject-error">
                {subjectError}
              </span>
            ) : null}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="send-email-to">
              To
            </label>
            <input
              id="send-email-to"
              className={`${styles.input} ${showErrors && toError ? styles.inputInvalid : ''}`}
              type="email"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              autoComplete="email"
              data-testid="send-email-to"
            />
            {showErrors && toError ? (
              <span className={styles.fieldError} data-testid="send-email-to-error">
                {toError}
              </span>
            ) : null}
          </div>

          {submitError ? (
            <p className={styles.formError} data-testid="send-email-error">
              {submitError}
            </p>
          ) : null}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onCancel}
              disabled={submitting}
              data-testid="send-email-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={submitting}
              data-testid="send-email-confirm"
            >
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
